import React, { useState, useEffect, useCallback } from 'react';
import './Activity.css';
import axios from 'axios';
import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow-right-icon.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/checkmark-square-icon.svg';

const AGENT_LABELS = {
  'forecast-agent': 'Forecast Agent',
  'copilot-agent': 'Chat Agent',
  'rollforward-agent': 'Rollforward Agent',
  'review-agent': 'Rollforward Agent',
};

const ACTION_LABELS = {
  'create': 'built',
  'update': 'updated',
  'roll': 'rolled forward',
};

const PAGE_SIZE = 10;

const AGENT_COLORS = {
  'forecast-agent': '#16a34a', // green
  'copilot-agent': '#2B7CEB', // current blue
  'rollforward-agent': '#7C3AED', // purple
  'review-agent': '#7C3AED', // purple

};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

function Activity({ forecastId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [openId, setOpenId] = useState(null);

  const fetchLogs = useCallback(async (cursor = null) => {
    if (!forecastId) return;

    const isInitial = !cursor;
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE });
      if (cursor) params.append('cursor', cursor);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/logs/${forecastId}?${params}`,
        { withCredentials: true }
      );

      const { logs: fetched = [], nextCursor: nc = null } = response.data || {};

      if (isInitial) {
        setLogs(fetched);
      } else {
        setLogs((prev) => [...prev, ...fetched]);
      }
      setNextCursor(nc);
    } catch (err) {
      console.error('Error fetching logs:', err);
      if (isInitial) setLogs([]);
    } finally {
      if (isInitial) setLoading(false);
      else setLoadingMore(false);
    }
  }, [forecastId]);

  useEffect(() => {
    setLogs([]);
    setNextCursor(null);
    fetchLogs();
  }, [fetchLogs]);

  const toggleOpen = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  if (loading) {
    return (
      <div className="sidebar-activity">
        <p className="sidebar-activity-empty">Loading activity...</p>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="sidebar-activity">
        <p className="sidebar-activity-empty">No activity yet.</p>
      </div>
    );
  }

  return (
    <div className="sidebar-activity">
      <div className="sidebar-activity-list">
        <div className="activity-timeline activity">
          {logs.map((log) => {
            const isOpen = openId === log.id;
            const agentLabel = AGENT_LABELS[log.agent] || log.agent || 'Agent';
            const actionLabel = ACTION_LABELS[log.action] || log.action || 'ran';
            const scenarioLabel = log.scenario || 'base';
            const isBase = scenarioLabel.toLowerCase() === 'base';
            const bulletStyle = { backgroundColor: AGENT_COLORS[log.agent] || 'rgba(0,0,0,0.12)' };

            return (
              <div className="activity-item" key={log.id}>
                <div className="bullet" style={bulletStyle} />
                <div className="activity-header" onClick={() => toggleOpen(log.id)}>
                  <span className="title">
                    <span className="agent">{agentLabel}</span> {actionLabel}{' '}
                    <span className="objective">
                      {isBase
                        ? 'the Base Cash Forecast'
                        : <>the forecast scenario '{scenarioLabel}'</>}
                    </span>.{' '}
                    <span className="date">{formatDate(log.createdAt)}</span>
                  </span>
                  <ArrowIcon className={`arrow ${isOpen ? 'open' : ''}`} />
                </div>
                {isOpen && (
                  <div className="activity-details">
                    <div className="activity-meta">
                      <div className="meta-row">
                        <span className="meta-label">When</span>
                        <span className="meta-value">{formatDateTime(log.createdAt)}</span>
                      </div>
                      <div className="meta-row">
                        <span className="meta-label">Status</span>
                        <span className={`meta-status ${log.success ? 'success' : 'failed'}`}>
                          {log.success ? 'Success' : 'Failed'}
                        </span>
                      </div>
                    </div>
                    {Array.isArray(log.notes) && log.notes.length > 0 && (
                      <div className="activity-logs">
                        {log.notes.map((note, idx) => (
                          <div className="activity-log-item" key={idx}>
                            <CheckIcon className="check" />
                            <span>{note}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {nextCursor && (
          <button
            className="show-more-btn"
            onClick={() => fetchLogs(nextCursor)}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Show More'}
          </button>
        )}
      </div>
    </div>
  );
}

export default Activity;
